import type { AuthenticatedUserContract, Locale } from "@chams-pay/contracts";
import { operationsMessages, translate } from "@chams-pay/contracts";
import { Block, Page } from "konsta/react";
import { ChevronLeft, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { screenTitle, workspaceNavigation, type WorkspaceScreen } from "../core/navigation.js";
import type { ServerSessionState } from "../core/use-session.js";
import {
  useDashboard,
  useInstruments,
  useReports,
  type InstrumentStatusFilter,
} from "../core/use-operations.js";
import {
  DEFAULT_STUDENT_FILTERS,
  type StudentFilters,
  type StudentSearchController,
} from "../core/use-students.js";
import type { WorkspaceController } from "../core/use-workspace.js";
import {
  relativeAgeLabel,
  updateBannerSupersedesServerSessionNotice,
  workspaceBanner,
} from "../core/use-workspace.js";
import { MobileDashboardOffline, MobileDashboardScreen } from "./dashboard.js";
import { MobileFeedbackPill, showMobileFeedback } from "./app-feedback.js";
import { MobileInstrumentsScreen, MobileReportsScreen, MobileSettingsScreen } from "./screens.js";
import {
  MobileAccountSheet,
  MobileAppBar,
  MobileDashboardPeriodPicker,
  MobileQuickActionsFab,
  MobileTabbar,
  MobileWorkspaceBanner,
} from "./shell.js";
import { MobilePullToRefresh } from "./pull-to-refresh.js";
import {
  MobileStudentIdentityHeaderTools,
  MobileStudentsHeaderTools,
  MobileStudentsScreen,
} from "./students.js";

type MessageKey = Parameters<typeof translate>[1];

function MobileServerSessionNotice({
  locale,
  state,
  onRetry,
  onSignIn,
}: {
  readonly locale: Locale;
  readonly state: Exclude<ServerSessionState, "ready">;
  readonly onRetry: () => void;
  readonly onSignIn: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const unreachable = state === "unreachable";
  const restoring = state === "restoring";
  return (
    <Block
      strong
      inset
      className="!bg-warning-soft flex flex-col gap-3 !text-warning"
      role="status"
    >
      <span className="flex flex-col gap-1">
        <strong className="font-heading text-sm">
          {t(
            restoring
              ? "entry.sessionRestoringTitle"
              : unreachable
                ? "entry.serverUnreachableTitle"
                : "entry.onlineSessionRequiredTitle",
          )}
        </strong>
        <span className="text-xs leading-relaxed">
          {t(
            restoring
              ? "entry.sessionRestoringBody"
              : unreachable
                ? "entry.serverUnreachableBody"
                : "entry.onlineSessionRequiredBody",
          )}
        </span>
      </span>
      {restoring ? (
        <RefreshCw aria-hidden="true" className="size-4 animate-spin" />
      ) : (
        <span className="flex flex-wrap gap-2">
          {unreachable && (
            <button
              type="button"
              onClick={onRetry}
              className="chams-focusable min-h-11 rounded-xl border border-warning-border bg-white px-4 font-heading text-xs font-bold"
            >
              {t("ops.retry")}
            </button>
          )}
          <button
            type="button"
            onClick={onSignIn}
            className="chams-focusable min-h-11 rounded-xl bg-primary px-4 font-heading text-xs font-bold text-white"
          >
            {t("entry.signInOnline")}
          </button>
        </span>
      )}
    </Block>
  );
}

/** Feeds the tab-bar badge without owning the cheques screen. */
function InstrumentCountProbe({
  csrf,
  onCount,
}: {
  readonly csrf: string;
  readonly onCount: (count: number) => void;
}) {
  const controller = useInstruments(csrf);
  const count = controller.items.filter((item) => item.status === "pending").length;
  useEffect(() => {
    onCount(count);
  }, [count, onCount]);
  return null;
}

function DashboardHost({
  locale,
  user,
  academicYear,
  blocked,
  online,
  workspace,
  banner,
  onOpenSync,
  onLocaleChange,
  onOpenStudents,
  onOpenInstruments,
  refreshRevision,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly academicYear: string | undefined;
  readonly blocked: boolean;
  readonly online: boolean;
  readonly workspace: WorkspaceController;
  readonly banner: React.ReactNode;
  readonly onOpenSync: () => void;
  readonly onLocaleChange: () => void;
  readonly onOpenStudents: (filters?: StudentFilters) => void;
  readonly onOpenInstruments: () => void;
  readonly refreshRevision: number;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const controller = useDashboard();

  useEffect(() => {
    if (refreshRevision === 0) return;
    void controller.reload();
  }, [controller.reload, refreshRevision]);

  return (
    <>
      <MobileAppBar
        locale={locale}
        user={user}
        title={t("dash.dashTitle")}
        blocked={blocked}
        online={online}
        workspace={workspace}
        onOpenSync={onOpenSync}
        onLocaleChange={onLocaleChange}
      />
      {banner}
      <MobileDashboardScreen
        locale={locale}
        controller={controller}
        blocked={blocked}
        activityPeriodSelector={
          <MobileDashboardPeriodPicker
            locale={locale}
            academicYear={controller.dashboard?.currentAcademicYear ?? academicYear}
            from={controller.from}
            to={controller.to}
            onFrom={controller.setFrom}
            onTo={controller.setTo}
          />
        }
        onOpenStudents={onOpenStudents}
        onOpenInstruments={onOpenInstruments}
      />
    </>
  );
}

function InstrumentsHost({
  locale,
  csrf,
  onOpenStudent,
  refreshRevision,
  initialFilter,
  onFilterChange,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly onOpenStudent: (studentId: string) => void;
  readonly refreshRevision: number;
  readonly initialFilter: InstrumentStatusFilter;
  readonly onFilterChange: (filter: InstrumentStatusFilter) => void;
}) {
  const controller = useInstruments(csrf, {
    initialFilter,
    onFilterChange,
    onTransitioned: (action) =>
      showMobileFeedback(action === "settle" ? "instrument-settled" : "instrument-rejected"),
  });
  useEffect(() => {
    if (refreshRevision === 0) return;
    void controller.reload();
  }, [controller.reload, refreshRevision]);
  return (
    <MobileInstrumentsScreen
      locale={locale}
      controller={controller}
      onOpenStudent={onOpenStudent}
    />
  );
}

function ReportsHost({
  locale,
  refreshRevision,
}: {
  readonly locale: Locale;
  readonly refreshRevision: number;
}) {
  const controller = useReports();
  useEffect(() => {
    if (refreshRevision === 0) return;
    void controller.reload();
  }, [controller.reload, refreshRevision]);
  return <MobileReportsScreen locale={locale} controller={controller} />;
}

export function MobileWorkspace({
  locale,
  user,
  csrf,
  online,
  networkOnline,
  serverSession,
  screen,
  onScreenChange,
  workspace,
  students,
  updateReady,
  updateBlocked,
  updateActivating,
  onActivateUpdate,
  onLocaleChange,
  onLock,
  onLogout,
  onSignInOnline,
  onRetryServerSession,
  onOpenInstrumentStudent,
  onStudentBack,
  onStudentListOpen,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly csrf: string | null;
  readonly online: boolean;
  readonly networkOnline: boolean;
  readonly serverSession: ServerSessionState;
  readonly screen: WorkspaceScreen;
  readonly onScreenChange: (screen: WorkspaceScreen) => void;
  readonly workspace: WorkspaceController;
  readonly students: StudentSearchController;
  readonly updateReady: boolean;
  readonly updateBlocked: boolean;
  readonly updateActivating: boolean;
  readonly onActivateUpdate: () => void;
  readonly onLocaleChange: () => void;
  readonly onLock: () => void;
  readonly onLogout: () => void;
  readonly onSignInOnline: () => void;
  readonly onRetryServerSession: () => void;
  readonly onOpenInstrumentStudent: (studentId: string) => void;
  readonly onStudentBack: () => void;
  readonly onStudentListOpen: () => void;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const operationsText = operationsMessages(locale);
  const navigation = workspaceNavigation(locale, user);
  const [accountOpen, setAccountOpen] = useState(false);
  const [instrumentCount, setInstrumentCount] = useState(0);
  const [instrumentFilter, setInstrumentFilter] = useState<InstrumentStatusFilter>("pending");
  const [registrationRequested, setRegistrationRequested] = useState(false);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const blocked = workspace.captureBlocked;
  const refreshContent = useCallback(async () => {
    if (workspace.canSync) await workspace.sync();
    else await workspace.refreshLocalStatus();
    if (screen === "students" && students.selected && online)
      await students.refreshSelected().catch(() => undefined);
    setRefreshRevision((value) => value + 1);
  }, [online, screen, students, workspace]);

  const bannerKind = workspaceBanner({
    captureBlocked: blocked,
    online: networkOnline,
    syncFailed: workspace.syncFailed,
    updateReady,
    updateBlocked,
  });
  const banner = (
    <MobileWorkspaceBanner
      locale={locale}
      kind={bannerKind}
      workspace={workspace}
      updateActivating={updateActivating}
      onActivateUpdate={onActivateUpdate}
    />
  );
  const sessionNotice =
    serverSession !== "ready" && !updateBannerSupersedesServerSessionNotice(bannerKind) ? (
      <MobileServerSessionNotice
        locale={locale}
        state={serverSession}
        onRetry={onRetryServerSession}
        onSignIn={onSignInOnline}
      />
    ) : null;
  const openStudents = (filters?: StudentFilters) => {
    onStudentListOpen();
    students.clearSelection();
    if (filters) students.setQuery("");
    students.setFilters(filters ?? DEFAULT_STUDENT_FILTERS);
    onScreenChange("students");
  };
  const selectMainScreen = useCallback(
    (next: WorkspaceScreen) => {
      if (next === "students") {
        onStudentListOpen();
        students.clearSelection();
      }
      onScreenChange(next);
    },
    [onScreenChange, onStudentListOpen, students.clearSelection],
  );
  return (
    <>
      <Page className="chams-page app-shell bg-canvas" data-testid="app-shell">
        {online && csrf && <InstrumentCountProbe csrf={csrf} onCount={setInstrumentCount} />}
        <MobilePullToRefresh locale={locale} onRefresh={refreshContent} />

        <>
          {screen === "dashboard" && online ? (
            <DashboardHost
              locale={locale}
              user={user}
              academicYear={workspace.replica.students[0]?.enrollment.academicYear}
              blocked={blocked}
              online={online}
              workspace={workspace}
              banner={banner}
              onOpenSync={() => setAccountOpen(true)}
              onLocaleChange={onLocaleChange}
              onOpenStudents={openStudents}
              onOpenInstruments={() => onScreenChange("instruments")}
              refreshRevision={refreshRevision}
            />
          ) : (
            <>
              <MobileAppBar
                locale={locale}
                user={user}
                title={
                  screen === "students" && students.selected ? (
                    <span className="flex min-w-0 flex-col items-start gap-0.5">
                      <strong className="w-full truncate text-base">
                        {locale === "ar" ? students.selected.nameAr : students.selected.nameFr}
                      </strong>
                      <small className="w-full truncate text-[11px] font-normal text-white/70">
                        {locale === "ar" ? students.selected.nameFr : students.selected.nameAr}
                      </small>
                    </span>
                  ) : (
                    <span className="block truncate">{screenTitle(screen, locale, user)}</span>
                  )
                }
                blocked={blocked}
                online={online}
                workspace={workspace}
                onOpenSync={() => setAccountOpen(true)}
                onLocaleChange={onLocaleChange}
                leading={
                  screen === "students" && students.selected ? (
                    <button
                      type="button"
                      aria-label={t("students.back")}
                      className="back-button grid size-11 shrink-0 place-items-center rounded-full bg-white/15"
                      onClick={onStudentBack}
                    >
                      <ChevronLeft aria-hidden="true" className="size-5 rtl:-scale-x-100" />
                    </button>
                  ) : undefined
                }
                segmented={
                  screen === "students" ? (
                    students.selected ? (
                      <MobileStudentIdentityHeaderTools
                        locale={locale}
                        student={students.selected}
                      />
                    ) : (
                      <MobileStudentsHeaderTools
                        locale={locale}
                        students={students}
                        workspace={workspace}
                      />
                    )
                  ) : undefined
                }
              />
              {banner}
              {sessionNotice}

              {screen === "students" && (
                <MobileStudentsScreen
                  locale={locale}
                  online={online}
                  csrf={csrf}
                  user={user}
                  students={students}
                  workspace={workspace}
                  registrationRequested={registrationRequested}
                  onRegistrationRequestHandled={() => setRegistrationRequested(false)}
                />
              )}
              {screen === "dashboard" && !online && (
                <MobileDashboardOffline
                  locale={locale}
                  lastSyncLabel={relativeAgeLabel(workspace.syncAgeMilliseconds, locale)}
                  pendingCount={workspace.pendingCount}
                  readinessTier={workspace.readinessTier}
                  onTakePayment={() => {
                    openStudents();
                    showMobileFeedback("choose-student");
                  }}
                  onOpenStudents={() => openStudents()}
                />
              )}
              {screen === "instruments" && online && csrf && (
                <InstrumentsHost
                  locale={locale}
                  csrf={csrf}
                  onOpenStudent={onOpenInstrumentStudent}
                  refreshRevision={refreshRevision}
                  initialFilter={instrumentFilter}
                  onFilterChange={setInstrumentFilter}
                />
              )}
              {screen === "instruments" && online && !csrf && (
                <Block
                  strong
                  inset
                  className="empty-state flex flex-col items-center gap-3 text-center"
                >
                  <strong className="font-heading text-base">
                    {t("entry.onlineSessionRequiredTitle")}
                  </strong>
                  <p className="text-sm text-muted-foreground">
                    {t("entry.onlineSessionRequiredBody")}
                  </p>
                  <button
                    type="button"
                    onClick={onSignInOnline}
                    className="min-h-11 rounded-lg bg-primary px-5 font-heading text-sm font-bold text-white"
                  >
                    {t("entry.signInOnline")}
                  </button>
                </Block>
              )}
              {screen === "reports" && online && (
                <ReportsHost locale={locale} refreshRevision={refreshRevision} />
              )}
              {screen !== "students" &&
                screen !== "settings" &&
                screen !== "dashboard" &&
                !online && (
                  <Block
                    strong
                    inset
                    className="empty-state text-center text-sm text-muted-foreground"
                  >
                    {operationsText.onlineOnly}
                  </Block>
                )}
              {screen === "settings" && (
                <MobileSettingsScreen
                  locale={locale}
                  user={user}
                  csrf={csrf}
                  refreshRevision={refreshRevision}
                  onAdvanceYearActivated={() => void workspace.sync()}
                />
              )}
            </>
          )}
        </>
      </Page>
      <MobileFeedbackPill locale={locale} />

      {!(screen === "students" && students.selected) && (
        <MobileQuickActionsFab
          locale={locale}
          paymentDisabled={blocked || !students.paymentActorId}
          registrationDisabled={!user || !online || !csrf}
          onTakePayment={() => {
            openStudents();
            showMobileFeedback("choose-student");
          }}
          onRegisterStudent={() => {
            setRegistrationRequested(true);
            openStudents();
          }}
        />
      )}

      <MobileTabbar
        label={t("app.brand")}
        locale={locale}
        items={navigation}
        activeScreen={screen}
        instrumentCount={instrumentCount}
        onSelect={selectMainScreen}
      />

      <MobileAccountSheet
        locale={locale}
        user={user}
        online={online}
        opened={accountOpen}
        workspace={workspace}
        onClose={() => setAccountOpen(false)}
        onSettings={() => onScreenChange("settings")}
        onLock={onLock}
        onLogout={onLogout}
      />
    </>
  );
}
